import { formatDate, money } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { CompanyInfo, InvoiceDetail } from '@/types';

/**
 * ── The PDF-preview fix ─────────────────────────────────────────────────────
 *
 * In v1 the "preview" was a client-side `@react-pdf/renderer` build piped into
 * an `<iframe>`, which is where the ~10 second black box came from:
 *
 *   1. an effect bug meant generation only started on the *second* render pass;
 *   2. it re-fetched entries and credits the page already had;
 *   3. `@react-pdf/renderer` cold-starts a layout engine (Yoga/WASM + fontkit)
 *      on first use, in the main bundle, on the main thread;
 *   4. `Font.register` pointed Helvetica at a fabricated gstatic woff2 URL, and
 *      react-pdf blocks layout until font resolution finishes or times out;
 *   5. once a blob URL finally existed, the iframe swapped in with no onLoad
 *      gate — so Chrome's PDF plugin painted an opaque dark viewport while it
 *      initialized.
 *
 * v2 renders the invoice as ordinary HTML from data that is already in the
 * query cache. It paints in the same frame as the rest of the page, reflows
 * instantly when an entry is edited, prints correctly via `@media print`, and
 * is selectable/searchable/screen-reader accessible — none of which a PDF in an
 * iframe is.
 *
 * The real PDF is now only built when someone actually wants a file (download
 * or email), behind a dynamic import so the layout engine never touches the
 * initial bundle. See `pdf.ts`.
 */

export interface InvoiceDocumentProps {
  invoice: InvoiceDetail;
  company: CompanyInfo | undefined;
  clientLabel: string;
  className?: string;
}

// Templates differ by accent color, rule weight and casing only. The header
// stays white in every template: the tinted band the non-simple ones used to
// have read as gray next to a plain invoice, and quick invoices (which take the
// team's default template) looked like a different product from the invoice
// list (whose rows mostly carry no template at all).
export const TEMPLATES = {
  simple: { accent: '#18181b', rule: '2px', uppercase: false },
  modern: { accent: '#4f46e5', rule: '3px', uppercase: true },
  professional: { accent: '#0f172a', rule: '1px', uppercase: true },
  creative: { accent: '#0d9488', rule: '4px', uppercase: false }
} as const;

export type TemplateId = keyof typeof TEMPLATES;

export function templateFor(invoice: InvoiceDetail) {
  return TEMPLATES[(invoice.templateId as TemplateId) ?? 'simple'] ?? TEMPLATES.simple;
}

export function isOverdue(invoice: InvoiceDetail) {
  return (
    invoice.status === 'sent' &&
    !invoice.paymentSubmittedAt &&
    invoice.dueDate !== null &&
    invoice.dueDate < new Date().toISOString().slice(0, 10)
  );
}

export function InvoiceDocument({
  invoice,
  company,
  clientLabel,
  className
}: InvoiceDocumentProps) {
  const template = templateFor(invoice);
  const overdue = isOverdue(invoice);

  const billTo = invoice.billTo;

  return (
    <article
      data-testid="invoice-document"
      className={cn(
        // A letter-proportioned sheet. `aspect` is not enforced — content grows
        // downward and prints across pages naturally.
        'mx-auto w-full max-w-[54rem] bg-white px-10 py-12 text-[13px] leading-relaxed text-zinc-900',
        'shadow-sm ring-1 ring-zinc-200 print:shadow-none print:ring-0',
        className
      )}
      style={{ ['--doc-accent' as string]: template.accent }}
    >
      {overdue ? (
        <div className="mb-8 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <p className="font-semibold text-red-800">Payment overdue</p>
          <p className="text-red-700">
            This invoice was due {formatDate(invoice.dueDate)}. Please remit payment at your
            earliest convenience.
          </p>
        </div>
      ) : null}

      <header
        className="flex flex-wrap items-start justify-between gap-6 pb-6"
        style={{ borderBottom: `${template.rule} solid ${template.accent}` }}
      >
        <div className="min-w-0">
          <h1
            className={cn(
              'text-xl font-semibold tracking-tight',
              template.uppercase && 'uppercase tracking-wide'
            )}
            style={{ color: template.accent }}
          >
            {company?.name ?? '—'}
          </h1>
          <address className="mt-1.5 whitespace-pre-line not-italic text-zinc-600">
            {[company?.addressLine1, company?.phone, company?.email]
              .filter(Boolean)
              .join('\n') || ' '}
          </address>
        </div>
        <div className="text-right">
          <p
            className={cn('text-lg font-semibold tracking-tight', template.uppercase && 'uppercase')}
          >
            {overdue ? 'Overdue invoice' : 'Invoice'}
          </p>
          <dl className="mt-2 space-y-0.5 text-zinc-600">
            <div className="flex justify-end gap-3">
              <dt>Number</dt>
              <dd className="font-medium tabular-nums text-zinc-900">{invoice.invoiceNumber}</dd>
            </div>
            <div className="flex justify-end gap-3">
              <dt>Issued</dt>
              <dd className="tabular-nums text-zinc-900">{formatDate(invoice.issueDate)}</dd>
            </div>
            <div className="flex justify-end gap-3">
              <dt>Due</dt>
              <dd className="tabular-nums text-zinc-900">{formatDate(invoice.dueDate)}</dd>
            </div>
          </dl>
        </div>
      </header>

      <section className="grid gap-8 py-6 sm:grid-cols-2">
        <div>
          <h2 className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
            Bill to
          </h2>
          {billTo ? (
            <address className="mt-1.5 not-italic">
              <p className="font-medium">
                {[billTo.name, billTo.title].filter(Boolean).join(', ')}
              </p>
              {billTo.organization ? <p>{billTo.organization}</p> : null}
              {billTo.address ? (
                <p className="whitespace-pre-line text-zinc-600">{billTo.address}</p>
              ) : null}
              {billTo.phone ? <p className="text-zinc-600">{billTo.phone}</p> : null}
              {billTo.emails.length ? (
                <p className="text-zinc-600">{billTo.emails.join(', ')}</p>
              ) : null}
            </address>
          ) : (
            <p className="mt-1.5 text-zinc-400">No billing party selected</p>
          )}
        </div>
        <div>
          <h2 className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
            {clientLabel}
          </h2>
          <p className="mt-1.5 font-medium">{invoice.clientName ?? '—'}</p>
          {invoice.periodStart && invoice.periodEnd ? (
            <p className="text-zinc-600">
              {formatDate(invoice.periodStart)} – {formatDate(invoice.periodEnd)}
            </p>
          ) : null}
        </div>
      </section>

      <table className="w-full border-collapse text-left">
        <thead>
          <tr className="border-y border-zinc-200 text-[11px] uppercase tracking-wider text-zinc-500">
            <th className="py-2 pr-3 font-semibold">Activity</th>
            <th className="w-24 py-2 pr-3 font-semibold">Date</th>
            <th className="w-20 py-2 pr-3 text-right font-semibold">Hours</th>
            <th className="w-24 py-2 pr-3 text-right font-semibold">Rate</th>
            <th className="w-28 py-2 text-right font-semibold">Amount</th>
          </tr>
        </thead>
        <tbody>
          {invoice.entries.map((entry) => {
            const rate = entry.hourlyRate ?? invoice.hourlyRate;
            return (
              <tr key={entry.id} className="border-b border-zinc-100 align-top">
                <td className="py-2 pr-3">
                  <span className="font-medium">{entry.activityName ?? 'Activity'}</span>
                  {entry.misc ? (
                    <span className="block text-zinc-500">{entry.misc}</span>
                  ) : null}
                </td>
                <td className="py-2 pr-3 tabular-nums text-zinc-600">
                  {entry.date ? formatDate(entry.date) : '—'}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">{entry.hours.toFixed(2)}</td>
                <td className="py-2 pr-3 text-right tabular-nums text-zinc-600">{money(rate)}</td>
                <td className="py-2 text-right tabular-nums">{money(entry.hours * rate)}</td>
              </tr>
            );
          })}

          {invoice.creditsAndFees.map((item) => (
            <tr key={item.id} className="border-b border-zinc-100">
              <td className="py-2 pr-3 font-medium">{item.description}</td>
              <td className="py-2 pr-3 tabular-nums text-zinc-600">
                {item.date ? formatDate(item.date) : '—'}
              </td>
              <td className="py-2 pr-3 text-right text-zinc-400">—</td>
              <td className="py-2 pr-3 text-right text-zinc-400">—</td>
              <td
                className={cn(
                  'py-2 text-right tabular-nums',
                  item.kind === 'credit' && 'text-emerald-700'
                )}
              >
                {item.kind === 'credit' ? `-${money(item.amount)}` : money(item.amount)}
              </td>
            </tr>
          ))}

          {invoice.entries.length === 0 && invoice.creditsAndFees.length === 0 ? (
            <tr>
              <td colSpan={5} className="py-8 text-center text-zinc-400">
                No line items on this invoice yet.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      <section className="mt-6 flex justify-end">
        <dl className="w-full max-w-xs space-y-1.5">
          <Row label="Subtotal" value={money(invoice.subtotal)} />
          {invoice.feesTotal > 0 ? <Row label="Fees" value={money(invoice.feesTotal)} /> : null}
          {invoice.creditsTotal > 0 ? (
            <Row label="Credits" value={`-${money(invoice.creditsTotal)}`} positive />
          ) : null}
          {invoice.lateInterest ? (
            <Row
              label={`Late interest (${invoice.lateInterest.rate}% per ${invoice.lateInterest.period})`}
              value={money(invoice.lateInterest.amount)}
            />
          ) : null}
          <div
            className="flex items-baseline justify-between gap-6 pt-2"
            style={{ borderTop: `${template.rule} solid ${template.accent}` }}
          >
            <dt className="font-semibold">Total due</dt>
            <dd className="text-lg font-semibold tabular-nums">{money(invoice.amountDue)}</dd>
          </div>
          <p className="pt-1 text-right tabular-nums text-zinc-500">
            {invoice.totalHours.toFixed(2)} hours at {money(invoice.hourlyRate)}/hr
          </p>
        </dl>
      </section>

      {invoice.notes ? (
        <section className="mt-8 border-t border-zinc-200 pt-4">
          <h2 className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
            Notes
          </h2>
          <p className="mt-1.5 whitespace-pre-line text-zinc-600">{invoice.notes}</p>
        </section>
      ) : null}

      <footer className="mt-8 border-t border-zinc-200 pt-4 text-zinc-500">
        <p>
          Payment is due by {formatDate(invoice.dueDate)}. Please reference invoice{' '}
          {invoice.invoiceNumber} with your payment.
        </p>
      </footer>
    </article>
  );
}

function Row({
  label,
  value,
  positive
}: {
  label: string;
  value: string;
  positive?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-6">
      <dt className="text-zinc-600">{label}</dt>
      <dd className={cn('tabular-nums', positive && 'text-emerald-700')}>{value}</dd>
    </div>
  );
}
